import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";

import { useDevices, useLogout, useMe } from "./api/hooks.js";
import { PageState } from "./components/PageState.js";
import { AudioConsentPage } from "./pages/AudioConsentPage.js";
import { AiNannyPage } from "./pages/AiNannyPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { DevicesPage } from "./pages/DevicesPage.js";
import { GameLibraryPage } from "./pages/GameLibraryPage.js";
import { LiveControlsPage } from "./pages/LiveControlsPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { SchedulesPage } from "./pages/SchedulesPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { UsageHistoryPage } from "./pages/UsageHistoryPage.js";
import { AlertsPage } from "./pages/AlertsPage.js";
import { SystemDiagnosticsPage } from "./pages/SystemDiagnosticsPage.js";
import { useState } from "react";

const NAV = [
  { to: "/", label: "Overview" },
  { to: "/devices", label: "Devices" },
  { to: "/library", label: "Game Library" },
  { to: "/schedules", label: "Schedules" },
  { to: "/live", label: "Live Controls" },
  { to: "/usage", label: "Usage & History" },
  { to: "/audio", label: "Audio & Consent" },
  { to: "/nanny", label: "AI Nanny" },
  { to: "/alerts", label: "Alerts" },
  { to: "/system", label: "Diagnostics" },
  { to: "/settings", label: "Settings" },
];

export function App() {
  const me = useMe();
  const devices = useDevices(!!me.data);
  const logout = useLogout();
  const navigate = useNavigate();
  const [deviceId, setDeviceId] = useState<string>("");

  if (me.isPending) return <p className="muted">Loading session…</p>;
  if (!me.data) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const recentDevice = [...(devices.data ?? [])].sort((a, b) =>
    (b.last_seen_at ?? "").localeCompare(a.last_seen_at ?? "")) [0];
  const selected = deviceId || recentDevice?.id || "";
  const role = (me.data as { role: string }).role;

  return (
    <div className="app">
      <header className="topbar">
        <h1>QuestGuardian</h1>
        <select
          aria-label="Select device"
          value={selected}
          onChange={(e) => setDeviceId(e.target.value)}
          disabled={!devices.data?.length}
        >
          {(devices.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.nickname} — {d.operational_state}
            </option>
          ))}
          {!devices.data?.length ? <option>{devices.isPending ? "Loading devices…" :
            devices.isError ? "Unable to load devices" : "No devices enrolled"}</option> : null}
        </select>
        <span className="muted">{(me.data as { email: string }).email} ({role})</span>
        <button
          onClick={() => {
            logout.mutate();
            navigate("/login");
          }}
        >
          Log out
        </button>
      </header>
      <nav className="nav" aria-label="Dashboard sections">
        {NAV.map((n) => (
          <RouteLink key={n.to} to={n.to} label={n.label} />
        ))}
      </nav>
      <main>
        <PageState query={devices}>
          <Routes>
            <Route path="/" element={<DashboardPage deviceId={selected} />} />
            <Route path="/devices" element={<DevicesPage deviceId={selected} />} />
            <Route path="/library" element={<GameLibraryPage deviceId={selected} />} />
            <Route path="/schedules" element={<SchedulesPage deviceId={selected} />} />
            <Route path="/live" element={<LiveControlsPage deviceId={selected} />} />
            <Route path="/usage" element={<UsageHistoryPage deviceId={selected} />} />
            <Route path="/audio" element={<AudioConsentPage deviceId={selected} />} />
            <Route path="/nanny" element={<AiNannyPage deviceId={selected} />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/system" element={<SystemDiagnosticsPage />} />
            <Route path="/settings" element={<SettingsPage role={role} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PageState>
      </main>
    </div>
  );
}

function RouteLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} end={to === "/"} className={({ isActive }) => (isActive ? "active" : "")}>
      {label}
    </NavLink>
  );
}
